import { useCustomMutation, useInvalidate, useOne } from "@refinedev/core";
import {
  MediaProjectResponse,
  MediaProjectInstanceResponse,
  ProjectInstanceSettingsRequest,
  ProjectMediaResponse,
  PublicationAction,
} from "pages/media/types";
import { useCallback } from "react";
import { useOptimisticUpdate } from "../../../../hooks/useOptimisticUpdate";
import { usePublicationStatus } from "./usePublicationStatus";
import { produce } from "immer";
import { AxiosError } from "axios";
import { computeIsMediaInFlight } from "../helpers/helper";

const changeInstanceStatusErrorMessage: Record<PublicationAction, string> = {
  Publish: "Failed to publish the project instance.",
  Unpublish: "Failed to unpublish the project instance.",
  MarkAsOutOfDate: "Failed to mark the project instance as out of date.",
  Expire: "Failed to expire the project instance.",
};

export const useProjectInstance = ({
  projectId,
  instanceId,
  mediaId,
}: {
  projectId: string;
  instanceId: string;
  mediaId?: string;
}) => {
  const { mutateAsync, isPending: isMutating } = useCustomMutation();
  const invalidate = useInvalidate();

  const { data: instanceData, isLoading } =
    useOne<MediaProjectInstanceResponse>({
      resource: `media/projects/${projectId}/instances`,
      id: instanceId,
      queryOptions: {
        // make sure the media has an id to avoid 404 API errors
        //  (e.g. when the project has no script yet cf MED-4595)
        enabled: Boolean(projectId) && Boolean(instanceId) && Boolean(mediaId),
      },
      errorNotification: false,
    });
  const instance = instanceData?.data;

  const invalidateProject = useCallback(async () => {
    await invalidate({
      resource: `media/projects`,
      id: projectId,
      invalidates: ["detail"],
    });
  }, [projectId, invalidate]);

  const { data: projectData, isLoading: isLoadingProject } =
    useOne<MediaProjectResponse>({
      resource: `media/projects`,
      id: projectId,
      errorNotification: false,
      queryOptions: {
        enabled: Boolean(projectId),
      },
    });
  const project = projectData?.data;
  const media = project?.media.find((x) => x.instance_id === instanceId);

  const {
    publicationStatus,
    isLoadingPublicationStatus,
    refetchPublicationStatus,
    publishedToastId,
    dismissPublishedToast,
  } = usePublicationStatus({ projectId, instanceId });
  const isLive = Boolean(publicationStatus?.has_live_publication);
  // Narrower than ProjectPageNew's "generating" spinner check: `Draft` is
  // "no video to show" but not "a regeneration is coming" (see MED-7156).
  const isMediaInFlight = computeIsMediaInFlight(
    project?.status,
    media?.status
  );
  // Live, but a later edit staled the published version. Not true while the
  // media is still mid-regeneration — republishing then would just push the
  // same stale content again, so the "Republish" prompt waits for the fresh
  // media to actually land (see MED-6969).
  const isPublicationOutOfDate =
    isLive && publicationStatus?.status === "OutOfDate" && !isMediaInFlight;
  const mediaStatus = media?.status;
  // The generated media itself is stale relative to the project's current
  // content — distinct from `isPublicationOutOfDate`, which tracks the *publication*.
  const isMediaOutOfDate = mediaStatus === "OutOfDate";
  // Combined visibility gate for the out-of-date widget/compact button —
  // computed once here so ExperiencePage and ProjectPageNew can't drift
  // (see MED-6969).
  const isOutOfDate = isMediaOutOfDate || isPublicationOutOfDate;

  const changeInstanceStatus = useCallback(
    async (action: PublicationAction) => {
      await mutateAsync({
        url: `media/projects/${projectId}/instances/${instanceId}/action?action=${encodeURIComponent(action)}`,
        method: "post",
        values: {},
        errorNotification: (error) => {
          if (!error) return false;
          const axiosError = error as unknown as AxiosError;
          if (axiosError.response?.status === 429) {
            return false;
          }
          return {
            message: changeInstanceStatusErrorMessage[action],
            type: "error" as const,
          };
        },
      });
      await invalidateProject();
      await refetchPublicationStatus();
    },
    [
      mutateAsync,
      projectId,
      instanceId,
      invalidateProject,
      refetchPublicationStatus,
    ]
  );

  const publish = useCallback(() => {
    return changeInstanceStatus("Publish");
  }, [changeInstanceStatus]);

  const unpublish = useCallback(() => {
    return changeInstanceStatus("Unpublish");
  }, [changeInstanceStatus]);

  // No-op if it's already out of date, or was never published in the first
  // place — nothing "live" exists yet for a setting change to be stale
  // against, so callers can fire this on every settings change without
  // worrying about spamming the endpoint or marking an unpublished instance.
  const markAsOutOfDate = useCallback(() => {
    if (
      publicationStatus?.status === "OutOfDate" ||
      !publicationStatus?.has_live_publication
    ) {
      return Promise.resolve();
    }
    return changeInstanceStatus("MarkAsOutOfDate");
  }, [
    changeInstanceStatus,
    publicationStatus?.status,
    publicationStatus?.has_live_publication,
  ]);

  // Flags the *generated media* (not the publication) as stale, telling the
  // user a regeneration is needed. Distinct from `markAsOutOfDate`: BE 412s
  // when the media is in a state that can't be marked, an expected no-op here.
  // Deliberately *not* gated on `has_live_publication` like `markAsOutOfDate`
  // is: generated-but-unpublished media still goes stale. BE only auto-marks
  // on a Slides asset PATCH, so callers that patch anything else (storyboard,
  // templates, media settings) must call this. The client-side checks below
  // keep them free to fire it without spamming the endpoint.
  const markMediaOutOfDate = useCallback(async () => {
    if (isMediaOutOfDate) return;
    // Nothing was ever generated — no media row, or `Draft`, which means no
    // media was ever requested. BE 404s on those, and the refetch below would
    // be wasted, so skip the round trip entirely: autosave calls this on every
    // save cycle while a project is still being built.
    if (!mediaStatus || mediaStatus === "Draft") return;
    try {
      await mutateAsync({
        url: `media/projects/${projectId}/instances/${instanceId}/media/out_of_date`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      });
    } catch {
      // expected no-op, see comment above
    }
    // Same as changeInstanceStatus: refresh the cache so isMediaOutOfDate
    // reflects the change, instead of relying on callers to remember to.
    await invalidateProject();
  }, [
    mutateAsync,
    projectId,
    instanceId,
    isMediaOutOfDate,
    mediaStatus,
    invalidateProject,
  ]);

  const projectQueryKey = [
    "default",
    "media/projects",
    "detail",
    projectId,
    {},
  ];

  const projectDataOptimisticUpdate = useOptimisticUpdate<{
    data: MediaProjectResponse;
  }>(projectQueryKey, (oldData, options: ProjectInstanceSettingsRequest) => {
    if (!oldData?.data) return oldData;

    return produce(oldData, (draft) => {
      draft.data.media
        .filter((mediaItem) => mediaItem.instance_id === instanceId)
        .forEach((mediaItem) => {
          if (options.include_document_animations != null) {
            mediaItem.include_document_animations =
              options.include_document_animations;
          }
          if (options.chat_mode != null) {
            mediaItem.chat_mode = options.chat_mode;
          }
          if (options.auto_select_music != null) {
            mediaItem.auto_select_music = options.auto_select_music;
          }
        });
    });
  });

  // Gates on `touchedKeys` rather than "value != null" (unlike the forward
  // update above), so a field whose previous value was itself null/undefined
  // still gets restored on rollback.
  const projectDataRollback = useOptimisticUpdate<{
    data: MediaProjectResponse;
  }>(
    projectQueryKey,
    (
      oldData,
      {
        touchedKeys,
        previousValues,
      }: {
        touchedKeys: Set<keyof ProjectInstanceSettingsRequest>;
        previousValues: ProjectInstanceSettingsRequest;
      }
    ) => {
      if (!oldData?.data) return oldData;

      return produce(oldData, (draft) => {
        draft.data.media
          .filter((mediaItem) => mediaItem.instance_id === instanceId)
          .forEach((mediaItem) => {
            if (touchedKeys.has("include_document_animations")) {
              mediaItem.include_document_animations =
                previousValues.include_document_animations as ProjectMediaResponse["include_document_animations"];
            }
            if (touchedKeys.has("chat_mode")) {
              mediaItem.chat_mode =
                previousValues.chat_mode as ProjectMediaResponse["chat_mode"];
            }
            if (touchedKeys.has("auto_select_music")) {
              mediaItem.auto_select_music = previousValues.auto_select_music;
            }
          });
      });
    }
  );

  const updateSettings = async (
    options: ProjectInstanceSettingsRequest,
    skipInvalidate?: boolean
  ) => {
    const touchedKeys = new Set<keyof ProjectInstanceSettingsRequest>();
    const previousValues: ProjectInstanceSettingsRequest = {};
    if (options.include_document_animations != null) {
      touchedKeys.add("include_document_animations");
      previousValues.include_document_animations =
        media?.include_document_animations;
    }
    if (options.chat_mode != null) {
      touchedKeys.add("chat_mode");
      previousValues.chat_mode = media?.chat_mode;
    }
    if (options.auto_select_music != null) {
      touchedKeys.add("auto_select_music");
      previousValues.auto_select_music = media?.auto_select_music;
    }

    // Optimistically update the cache before API call
    projectDataOptimisticUpdate(options);

    try {
      await mutateAsync({
        url: `media/projects/${projectId}/instances/${instanceId}/settings`,
        method: "patch",
        values: options,
      });
    } catch (error) {
      // Roll back only the fields this call touched, so the cache doesn't
      // keep lying about what's actually saved on the backend.
      projectDataRollback({ touchedKeys, previousValues });
      throw error;
    }

    if (!skipInvalidate) {
      await invalidateProject();
    }
  };

  const updateAccess = (restricted: boolean, skipInvalidate?: boolean) =>
    updateSettings(
      { publication_mode: restricted ? "PinByEmail" : "PublicLink" },
      skipInvalidate
    );

  const updateAuthorizedEmails = (emails: string[]) => {
    return mutateAsync({
      url: `media/projects/${projectId}/instances/${instanceId}/emails`,
      method: "patch",
      values: {
        emails,
      },
    });
  };

  // instance settings are stored in project media
  const instanceSettings = media;

  return {
    instance,
    project,
    media,
    publish,
    unpublish,
    markAsOutOfDate,
    markMediaOutOfDate,
    invalidateProject,
    updateAuthorizedEmails,
    updateSettings,
    updateAccess,
    instanceSettings,
    isLoading: isLoading || isLoadingPublicationStatus,
    isLoadingProject,
    // All three sources the out-of-date flags above are derived from: the
    // instance, the project (media's status lives here), and the publication
    // status. Callers that decide something *once* from those flags (e.g.
    // OutOfDatePublicationWidget) need to wait for all of them, not just
    // `isLoading` — most callers only need the latter, so this is separate
    // rather than folded into `isLoading` itself.
    isOutOfDateStatusLoading:
      isLoading || isLoadingProject || isLoadingPublicationStatus,
    isMutating,
    publicationStatus,
    isLive,
    isPublicationOutOfDate,
    isMediaOutOfDate,
    isOutOfDate,
    publishedToastId,
    dismissPublishedToast,
  };
};
